import { Service, signal, inject, computed } from '@angular/core'
import { AuthRegisterEntityBody, parseBool, UserInfo } from '@seiri/shared/common'
import {
  AuthApiService,
  LS_TOKEN,
  LoggerService,
  UsersApiService,
  defaultGetTokens,
  describeApiError,
} from '@seiri/shared/frontend'
import * as Sentry from '@sentry/angular'
import { WebsocketService } from '../../shared/services/websocket.service'

const LS_USER_DATA = 'seiri_user'
const LS_AS_OWNER = 'seiri_asOwner'

@Service()
export class AuthService {
  private readonly logger = inject(LoggerService).forContext('AuthService')
  private authService = inject(AuthApiService)
  private usersApi = inject(UsersApiService)
  private ws = inject(WebsocketService)

  private readonly _user = signal<UserInfo | null>(null)
  private readonly _asOwner = signal<boolean>(false)

  get permissions() {
    return computed(() => new Set(this.user()?.permissions || []))
  }

  get root() {
    return computed(() => this.user()?.root || false)
  }

  async logIn(email: string, password: string, abortSignal: AbortSignal): Promise<UserInfo> {
    try {
      const { accessToken } = await this.authService.signin({ email, password }, { signal: abortSignal })
      this.setToken(accessToken)
      const data = await this.usersApi.me()
      this.handleLoginRespone(data)
      return data
    } catch (error) {
      this.handleLoginError(error)
    }
  }

  async registerEntity(body: AuthRegisterEntityBody) {
    try {
      const { accessToken } = await this.authService.registerEntity(body)
      this.setAsOwner(true)
      this.setToken(accessToken)
      const data = await this.usersApi.me()
      this.handleLoginRespone(data)
      return data
    } catch (error) {
      this.handleLoginError(error)
    }
  }

  autoLogin() {
    try {
      const token = localStorage.getItem(LS_TOKEN)
      const asOwner = parseBool(localStorage.getItem(LS_AS_OWNER))
      if (!token) return
      const data = localStorage.getItem(LS_USER_DATA)
      if (!data) return
      const user = JSON.parse(data) as UserInfo
      if (!user) return
      this._asOwner.set(asOwner)
      this._user.set(user)
      this.wsAuthenticate()
      Sentry.setUser({ email: user.email, id: user.id })
      void this.refreshUser()
    } catch (error) {
      this.logger.error('session restore failed', error)
    }
  }

  /**
   * Re-reads the profile behind a restored session, so its permissions and premise are current: a stored profile
   * keeps whatever the user signed in with (docs/authorization.md). The stored one stays if this fails.
   */
  private async refreshUser() {
    try {
      this.setUser(await this.usersApi.me())
    } catch (error) {
      this.logger.warn('profile refresh failed', error)
    }
  }

  signOut() {
    localStorage.removeItem(LS_USER_DATA)
    localStorage.removeItem(LS_TOKEN)
    localStorage.removeItem(LS_AS_OWNER)
    this.ws.disconnect()
    window.location.assign('/account/sign-in')
  }

  resetPassword(email: string, abortSignal?: AbortSignal) {
    return this.authService.resetPassword({ email }, { signal: abortSignal })
  }

  setNewPassword(password: string, token: string, abortSignal?: AbortSignal) {
    return this.authService.setNewPassword({ password, token }, { signal: abortSignal })
  }

  setAsOwner(value: boolean) {
    localStorage.setItem(LS_AS_OWNER, value.toString())
    this._asOwner.set(value)
  }

  get asOwner() {
    return this._asOwner.asReadonly()
  }

  setUser(value: UserInfo | null) {
    if (value) {
      localStorage.setItem(LS_USER_DATA, JSON.stringify(value))
      Sentry.setUser({ email: value.email, id: value.id })
    } else {
      localStorage.removeItem(LS_USER_DATA)
      Sentry.setUser(null)
    }
    this._user.set(value)
  }

  get user() {
    return this._user.asReadonly()
  }

  private async wsAuthenticate() {
    const token = defaultGetTokens()
    await this.ws.authenticate(token)
  }

  setToken(token: string) {
    localStorage.setItem(LS_TOKEN, token)
  }

  private handleLoginRespone(user: UserInfo) {
    this.wsAuthenticate()
    this.setUser(user)
  }

  private handleLoginError(err: unknown): never {
    throw new Error(describeApiError(err))
  }
}
